import React from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { ImageSliderLogicReturn } from '../ImageSliderLogic';
import '../ImageSlider.css';

/**
 * ImageSlider 컴포넌트의 기본 스킨
 */
const BasicImageSliderSkin: React.FC<ImageSliderLogicReturn> = ({
    // Props
    sliderTitle,
    showTitle,
    imageObjectFit,
    images,
    infiniteSlide,
    slidesToShow,
    showNavigation,
    showIndicators,
    
    // State
    currentIndex,
    isEditingTitle,
    titleEditValue,
    extendedImages,
    slideWidth,
    
    // Refs
    sliderRef,
    containerRef,
    
    // Computed styles
    titleStyle,
    sliderContainerStyle,
    sliderStyle,
    className,
    
    // Functions
    goToNext,
    goToPrev,
    goToSlide,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handleTitleDoubleClick,
    handleTitleBlur,
    handleImageClick,
    setTitleEditValue,
    
    // Utils
    t
}) => {

  // 이미지가 없는 경우
  if (!extendedImages || extendedImages.length === 0) {
    return (
      <div 
        className={`image-slider-container empty ${className}`} 
        style={sliderContainerStyle}
        ref={containerRef}
      >
        {showTitle && (
          <div className="image-slider-header">
            {isEditingTitle ? (
              <input
                type="text"
                value={titleEditValue}
                onChange={(e) => setTitleEditValue(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyPress={(e) => e.key === 'Enter' && handleTitleBlur()}
                style={titleStyle}
                className="title-edit-input"
                autoFocus
              />
            ) : (
              <h2
                className="image-slider-title"
                style={titleStyle}
                onDoubleClick={handleTitleDoubleClick}
              >
                {sliderTitle}
              </h2>
            )}
          </div>
        )}
        <div className="image-slider-empty">
          <p>{t('표시할 이미지가 없습니다.')}</p>
          <p>{t('속성 패널에서 이미지를 추가해주세요.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`image-slider-container ${className}`}
      style={sliderContainerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      {/* 제목 */}
      {showTitle && (
        <div className="image-slider-header">
          {isEditingTitle ? (
            <input
              type="text"
              value={titleEditValue}
              onChange={(e) => setTitleEditValue(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyPress={(e) => e.key === 'Enter' && handleTitleBlur()}
              style={titleStyle}
              className="title-edit-input"
              autoFocus
            />
          ) : (
            <h2
              className="image-slider-title"
              style={titleStyle}
              onDoubleClick={handleTitleDoubleClick}
            >
              {sliderTitle}
            </h2>
          )}
        </div>
      )}

      {/* 슬라이더 메인 영역 */}
      <div className="image-slider-main">
        <div 
          className="image-slider-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            ref={sliderRef}
            className="image-slider-track"
            style={sliderStyle}
          >
            {extendedImages.map((image: any, index: number) => (
              <div 
                key={`${image.id}-${index}`} 
                className="image-slider-slide"
                style={{ width: slideWidth > 0 ? `${slideWidth}px` : 'auto' }}
              >
                <div
                  className="image-slider-image-container"
                  onClick={() => handleImageClick(image)}
                  style={{ cursor: image.link ? 'pointer' : 'default' }}
                >
                  <img
                    src={image.url}
                    alt={image.alt || image.title || `슬라이드 ${index + 1}`}
                    className="image-slider-image"
                    style={{ objectFit: imageObjectFit }}
                    loading="lazy"
                  />
                  {image.title && (
                    <div className="image-slider-image-overlay">
                      <h3 className="image-slider-image-title">{image.title}</h3>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 네비게이션 버튼 */}
        {showNavigation && extendedImages.length > 1 && (
          <>
            <button
              className="image-slider-nav image-slider-nav-prev"
              onClick={goToPrev}
              aria-label="이전 이미지"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="image-slider-nav image-slider-nav-next"
              onClick={goToNext}
              aria-label="다음 이미지"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}

        {/* 인디케이터 */}
        {showIndicators && images.length > 1 && (
          <div className="image-slider-indicators">
            {images.map((_: any, index: number) => {
              const currentSlideIndex = infiniteSlide 
                ? Math.max(0, Math.min(images.length - 1, currentIndex - slidesToShow))
                : currentIndex;
              
              return (
                <button
                  key={index}
                  className={`image-slider-indicator ${
                    currentSlideIndex === index ? 'active' : ''
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`${index + 1}번째 이미지로 이동`}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasicImageSliderSkin;